
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreHorizontal, Calendar, Tag } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TrelloCardDialog } from "./TrelloCardDialog";
import { KanbanCard } from "@/types/database";
import { format, isAfter, isBefore, parseISO, addDays } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Draggable } from "@hello-pangea/dnd";
import { useState } from "react";
import { CardDetailsDialog } from "./CardDetailsDialog";
import { toast } from "@/lib/toast";
import { useQuery } from "@tanstack/react-query";

interface TrelloCardItemProps {
  card: KanbanCard;
  boardId: string;
  columnId: string;
  onCardUpdated: () => void;
  index: number;
}

export function TrelloCardItem({
  card,
  boardId,
  columnId,
  onCardUpdated,
  index
}: TrelloCardItemProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(card.is_completed || false);
  
  // Buscar tags do cartão
  const { data: cardTags = [] } = useQuery({
    queryKey: ["card_tags", card.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_card_tags")
        .select("*")
        .eq("card_id", card.id);
        
      if (error) throw error;
      return data || [];
    }
  });
  
  // Function to handle card deletion
  const handleDeleteCard = async () => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      console.log("Deleting card:", card.id);
      
      const { error } = await supabase
        .from('kanban_cards')
        .delete()
        .eq('id', card.id);
      
      if (error) {
        console.error("Error deleting card:", error);
        toast.error("Erro ao excluir card");
        return;
      }
      
      console.log("Card deleted successfully");
      toast.success("Card excluído com sucesso");
      onCardUpdated();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error("Erro ao excluir card");
    } finally {
      setIsDeleting(false);
    }
  };

  // Function to handle completion toggle
  const handleToggleCompletion = async (checked: boolean) => {
    try {
      console.log('Toggling completion for card:', card.id, 'to:', checked);
      
      const { error } = await supabase
        .from('kanban_cards')
        .update({ 
          is_completed: checked,
          updated_at: new Date().toISOString()
        })
        .eq('id', card.id);
      
      if (error) {
        console.error('Error updating card completion:', error);
        throw error;
      }
      
      console.log('Card completion updated successfully');
      setIsCompleted(checked);
      onCardUpdated();
      toast.success(checked ? "Atividade marcada como concluída!" : "Atividade desmarcada");
    } catch (error) {
      console.error('Error updating card completion:', error);
      toast.error("Erro ao atualizar status da atividade");
    }
  };
  
  // Function to get due date status
  const getDueDateStatus = (dueDate: string | null, completed: boolean) => {
    if (!dueDate) return null;
    if (completed) return "completed"; // Nova condição para completados
    
    const today = new Date();
    const dueDateObj = parseISO(dueDate);
    const soonDate = addDays(today, 2); // Consider "soon" if due within 2 days
    
    if (isAfter(today, dueDateObj)) {
      return "overdue"; // Overdue
    } else if (isBefore(dueDateObj, soonDate)) {
      return "soon"; // Due soon
    } else {
      return "ok"; // Due later
    }
  };
  
  // Format the due date nicely
  const formatDueDate = (dueDate: string | null) => {
    if (!dueDate) return null;
    return format(parseISO(dueDate), "dd/MM/yyyy");
  };

  // Get color class based on due date status
  const getDueDateColorClass = (status: string | null) => {
    switch (status) {
      case "completed":
        return "text-green-500 font-medium";
      case "overdue":
        return "text-red-500 font-medium";
      case "soon":
        return "text-amber-500";
      case "ok":
        return "text-green-500";
      default:
        return "text-muted-foreground";
    }
  };
  
  // Function to extract the name from the assignee (which might be an email)
  const getAssigneeName = (assignee: string | undefined) => {
    if (!assignee) return '';
    
    // Check if it's an email and extract the part before @
    if (assignee.includes('@')) {
      return assignee.split('@')[0];
    }
    
    return assignee;
  };
  
  const dueDateStatus = getDueDateStatus(card.due_date, isCompleted);
  const dueDateColorClass = getDueDateColorClass(dueDateStatus);
  
  return (
    <Draggable draggableId={card.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
          }}
          className={`${snapshot.isDragging ? 'opacity-70' : ''}`}
        >
          <Card 
            className={`hover:shadow-md transition-all cursor-pointer overflow-hidden ${isCompleted ? 'opacity-75 bg-green-50' : ''}`}
            onClick={() => setIsDetailsOpen(true)}
          >
            {/* Capa do cartão */}
            {card.cover_image_url && (
              <div className="w-full h-32 bg-gray-100 overflow-hidden">
                <img 
                  src={card.cover_image_url} 
                  alt="Capa do cartão"
                  className="w-full h-full object-cover" 
                />
              </div>
            )}
            
            <CardContent className="p-3 space-y-2">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-start gap-2 flex-1">
                  <Checkbox
                    checked={isCompleted}
                    onCheckedChange={handleToggleCompletion}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1"
                  />
                  <h3 className={`text-sm font-medium line-clamp-2 ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                    {card.title}
                  </h3>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <TrelloCardDialog
                      mode="edit"
                      boardId={boardId}
                      columnId={columnId}
                      card={card}
                      onCardSaved={onCardUpdated}
                      trigger={
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          Editar
                        </DropdownMenuItem>
                      }
                    />
                    <DropdownMenuItem
                      className="text-red-500 focus:text-red-500"
                      onSelect={handleDeleteCard}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Excluindo..." : "Excluir"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {cardTags && cardTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {cardTags.map((tag) => (
                    <Badge
                      key={tag.id}
                      style={{
                        backgroundColor: tag.color,
                        color: 'white'
                      }}
                      className="px-2 py-0 h-5 text-xs font-normal"
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              )}
              
              {card.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {card.description}
                </p>
              )}
              
              <div className="flex justify-between items-center pt-1 text-xs">
                {card.assignee && (
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-gray-200 text-[10px] flex items-center justify-center">
                      {getAssigneeName(card.assignee).charAt(0).toUpperCase()}
                    </div>
                    <span className="text-muted-foreground">{getAssigneeName(card.assignee)}</span>
                  </div>
                )}
                
                {card.due_date && (
                  <div className={`flex items-center gap-1 ${dueDateColorClass}`}>
                    <Calendar className="h-3 w-3" />
                    <span>{formatDueDate(card.due_date)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Card Details Dialog for viewing/editing full card information */}
          <CardDetailsDialog
            isOpen={isDetailsOpen}
            setIsOpen={setIsDetailsOpen}
            card={card}
            columnId={columnId}
            onCardUpdated={onCardUpdated}
          />
        </div>
      )}
    </Draggable>
  );
}
